import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import withAuth from '../../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import { FiRefreshCcw, FiDownloadCloud, FiX, FiFileText, FiFile } from "react-icons/fi";
import { TabContext } from '../../components/TabbedLayout';
import { ReportEmbedContext } from '../reportEmbedContext';
import { GET_STOCK_ADJUSTMENT_REPORT, BASE_URL } from '../../utlilities/config';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';


// Enhanced Spinner Component
const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
        <p className="text-indigo-600 font-medium">Loading...</p>
      </div>
    </div>
);

// Helper function to format date in local timezone (IST)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Build initial filters
const buildInitialFilters = (filters = []) => {
  const today = new Date();
  const todayFormatted = formatLocalDate(today);

  return filters.reduce((accumulator, filter) => {
    if (filter.type === 'date') {
      const dateValue = todayFormatted;
      return { ...accumulator, [filter.id]: dateValue };
    }

    return { ...accumulator, [filter.id]: '' };
  }, {});
};

const ADJUSTMENT_TYPE_FILTERS = [
  { id: 'plus', label: 'Plus Qty' },
  { id: 'minus', label: 'Minus Qty' },
  { id: 'others', label: 'Others' },
];

const getRate = (item) => parseFloat(item.Rate || item.BatchPurchasePrice || 0);

const getPlusValue = (item) => parseFloat(item.PlusQty || 0) * getRate(item);

const getMinusValue = (item) => parseFloat(item.MinusQty || 0) * getRate(item);

const matchesAdjustmentTypeFilter = (item, selectedTypes) => {
  if (!selectedTypes || selectedTypes.length === 0) return true;

  const plusQty = parseFloat(item.PlusQty || 0);
  const minusQty = parseFloat(item.MinusQty || 0);
  const isPlus = plusQty > 0;
  const isMinus = minusQty > 0;
  const isOthers = !isPlus && !isMinus;

  return (
    (selectedTypes.includes('plus') && isPlus) ||
    (selectedTypes.includes('minus') && isMinus) ||
    (selectedTypes.includes('others') && isOthers)
  );
};

const filterByAdjustmentType = (data, selectedTypes) =>
  data.filter((item) => matchesAdjustmentTypeFilter(item, selectedTypes));

// Helper function to group data by InvoiceNo
const groupByInvoice = (data) => {
  const grouped = {};
  data.forEach(item => {
    const key = `${item.Sequence}_${item.InvoiceNo}`;
    if (!grouped[key]) {
      grouped[key] = {
        InvoiceNo: item.InvoiceNo,
        Sequence: item.Sequence,
        EntryDate: item.EntryDate,
        items: []
      };
    }
    grouped[key].items.push(item);
  });
  return Object.values(grouped);
};

// Report configuration
const reportConfig = {
  filters: [
    { id: 'fromDate', type: 'date', label: 'From Date' },
    { id: 'toDate', type: 'date', label: 'To Date' },
  ],
};

// Export Format Selection Modal
const ExportFormatModal = ({ isOpen, onClose, onSelectFormat }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-96 max-w-md transform transition-transform duration-300 ease-out">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex justify-between items-center rounded-t-2xl">
                    <h2 className="text-xl font-semibold text-white flex items-center">
                        <FiDownloadCloud className="mr-2" /> Export Report
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors duration-200"
                        aria-label="Close modal"
                    >
                        <FiX size={20} />
                    </button>
                </div>
                <div className="p-6">
                    <p className="text-sm text-slate-600 mb-4">Choose export format:</p>
                    <div className="flex flex-col gap-3">
                        <button
                            onClick={() => onSelectFormat('excel')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                                <FiFileText className="text-green-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to Excel</p>
                                <p className="text-xs text-slate-500">Download as .xlsx file</p>
                            </div>
                        </button>
                        <button
                            onClick={() => onSelectFormat('pdf')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
                                <FiFile className="text-red-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to PDF</p>
                                <p className="text-xs text-slate-500">Download as .pdf file</p>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const StockAdjustmentBillWise = () => {
    const tabContext = useContext(TabContext);
    const embedded = useContext(ReportEmbedContext);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(() => buildInitialFilters(reportConfig.filters));
    const [stockData, setStockData] = useState([]);
    const [selectedAdjustmentTypes, setSelectedAdjustmentTypes] = useState([]);
    const token = localStorage.getItem('accessToken');
    const [showExportModal, setShowExportModal] = useState(false);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(1000);


    const filteredStockData = useMemo(
        () => filterByAdjustmentType(stockData, selectedAdjustmentTypes),
        [stockData, selectedAdjustmentTypes]
    );

    const groupedData = useMemo(
        () => groupByInvoice(filteredStockData),
        [filteredStockData]
    );

    const handleFilterChange = (filterId, value) => {
        setFilters((prev) => ({
            ...prev,
            [filterId]: value,
        }));
    };

    const toggleAdjustmentType = (typeId) => {
        setSelectedAdjustmentTypes((prev) =>
            prev.includes(typeId)
                ? prev.filter((id) => id !== typeId)
                : [...prev, typeId]
        );
        setCurrentPage(1);
    };

    const handleRunReport = () => {
        getStockAdjustmentReport();
    };

    const handleExport = () => {
        // Check if filters are set
        if (!filters.fromDate || !filters.toDate) {
            toast.error('Please select date range before exporting', { autoClose: 2000 });
            return;
        }
        setShowExportModal(true);
    };

    const exportToExcel = async () => {
        setLoading(true);
        try {
            const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
            const toDate = filters.toDate ? new Date(filters.toDate) : null;
            
            const formatDate = (date) => {
                if (!date) return '';
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            toast.info('Fetching all data for export...', { autoClose: 2000 });
            
            // Fetch all data from API
            const response = await axios.get(`${BASE_URL}${GET_STOCK_ADJUSTMENT_REPORT}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    'rpType': 'bill',
                    'fromDate': formatDate(fromDate),
                    'toDate': formatDate(toDate),
                    'departmentID': localStorage.getItem('department_id') || '',
                },
            });

            if (response.status !== 200) {
                toast.error('Failed to fetch data for export', { autoClose: 2000 });
                setLoading(false);
                return;
            }

            const data = response.data.data || [];
            const filteredData = filterByAdjustmentType(data, selectedAdjustmentTypes);
            const groupedExportData = groupByInvoice(filteredData);

            // Flatten grouped data for Excel export
            const excelData = [];
            groupedExportData.forEach((invoice) => {
                invoice.items.forEach((item, itemIndex) => {
                    excelData.push({
                        'Entry No': invoice.InvoiceNo,
                        'Date': new Date(invoice.EntryDate).toLocaleDateString('en-GB'),
                        'SI No': itemIndex + 1,
                        'Item Name': item.HeaderItemName || '',
                        'Batch No': item.BatchNo || '',
                        'Expiry': item.ExpiryDate ? new Date(item.ExpiryDate).toLocaleDateString('en-GB', { month: '2-digit', year: 'numeric' }) : '',
                        'Old Stock': Number(parseFloat(item.CurrentStock || 0).toFixed(2)),
                        'Rate': Number(getRate(item).toFixed(2)),
                        'Plus Qty': Number(parseFloat(item.PlusQty || 0).toFixed(2)),
                        'Minus Qty': Number(parseFloat(item.MinusQty || 0).toFixed(2)),
                        'Plus Value': Number(getPlusValue(item).toFixed(2)),
                        'Minus Value': Number(getMinusValue(item).toFixed(2)),
                        'Adjusted Qty': Number(parseFloat(item.AdjustedQty || 0).toFixed(2)),
                        'Remarks': item.Remarks || '',
                    });
                });
            });

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);

            const colWidths = [
                { wch: 12 },  // Entry No
                { wch: 12 },  // Date
                { wch: 6 },   // SI No
                { wch: 30 },  // Item Name
                { wch: 12 },  // Batch No
                { wch: 10 },  // Expiry
                { wch: 12 },  // Old Stock
                { wch: 10 },  // Rate
                { wch: 12 },  // Plus Qty
                { wch: 12 },  // Minus Qty
                { wch: 12 },  // Plus Value
                { wch: 12 },  // Minus Value
                { wch: 12 },  // Adjusted Qty
                { wch: 20 },  // Remarks
            ];
            ws['!cols'] = colWidths;

            XLSX.utils.book_append_sheet(wb, ws, 'Stock Adjustment');

            const fromDateStr = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const toDateStr = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const filename = `Stock_Adjustment_Bill_Wise_${fromDateStr}_to_${toDateStr}.xlsx`;

            XLSX.writeFile(wb, filename);
            toast.success(`Excel file exported successfully with ${excelData.length} records`, { autoClose: 3000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.error('Failed to export to Excel', { autoClose: 2000 });
        } finally {
            setLoading(false);
        }
    };

    const exportToPDF = async () => {
        setLoading(true);
        try {
            const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
            const toDate = filters.toDate ? new Date(filters.toDate) : null;
            
            const formatDate = (date) => {
                if (!date) return '';
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            toast.info('Fetching all data for export...', { autoClose: 2000 });
            
            // Fetch all data from API
            const response = await axios.get(`${BASE_URL}${GET_STOCK_ADJUSTMENT_REPORT}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    'rpType': 'bill',
                    'fromDate': formatDate(fromDate),
                    'toDate': formatDate(toDate),
                    'departmentID': localStorage.getItem('department_id') || '',
                },
            });

            if (response.status !== 200) {
                toast.error('Failed to fetch data for export', { autoClose: 2000 });
                setLoading(false);
                return;
            }

            const data = response.data.data || [];
            const filteredData = filterByAdjustmentType(data, selectedAdjustmentTypes);
            const groupedExportData = groupByInvoice(filteredData);

            // Flatten grouped data for PDF export
            const tableData = [];
            groupedExportData.forEach((invoice) => {
                invoice.items.forEach((item, itemIndex) => {
                    tableData.push([
                        invoice.InvoiceNo,
                        new Date(invoice.EntryDate).toLocaleDateString('en-GB'),
                        itemIndex + 1,
                        item.HeaderItemName || '',
                        item.BatchNo || '',
                        item.ExpiryDate ? new Date(item.ExpiryDate).toLocaleDateString('en-GB', { month: '2-digit', year: 'numeric' }) : '',
                        parseFloat(item.CurrentStock || 0).toFixed(2),
                        getRate(item).toFixed(2),
                        parseFloat(item.PlusQty || 0).toFixed(2),
                        parseFloat(item.MinusQty || 0).toFixed(2),
                        getPlusValue(item).toFixed(2),
                        getMinusValue(item).toFixed(2),
                        parseFloat(item.AdjustedQty || 0).toFixed(2),
                        item.Remarks || '',
                    ]);
                });
            });

            const doc = new jsPDF('landscape', 'mm', 'a4');
            
            doc.setFontSize(16);
            doc.text('Stock Adjustment Bill Wise Report', 14, 15);
            
            doc.setFontSize(10);
            const fromDateStr = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB') : 'All';
            const toDateStr = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB') : 'All';
            doc.text(`Date Range: ${fromDateStr} to ${toDateStr}`, 14, 22);
            
            autoTable(doc, {
                head: [['Entry No', 'Date', 'SI', 'Item Name', 'Batch No', 'Expiry', 'Old Stock', 'Rate', 'Plus Qty', 'Minus Qty', 'Plus Value', 'Minus Value', 'Adjusted Qty', 'Remarks']],
                body: tableData,
                startY: 28,
                styles: { fontSize: 6, cellPadding: 1 },
                headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [249, 250, 251] },
                margin: { top: 28, left: 14, right: 14 },
            });

            const filename = `Stock_Adjustment_Bill_Wise_${fromDateStr.replace(/\//g, '-')}_to_${toDateStr.replace(/\//g, '-')}.pdf`;
            doc.save(filename);
            toast.success(`PDF file exported successfully with ${tableData.length} records`, { autoClose: 3000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            toast.error('Failed to export to PDF', { autoClose: 2000 });
        } finally {
            setLoading(false);
        }
    };

    const handleExportFormat = (format) => {
        if (format === 'excel') {
            exportToExcel();
        } else if (format === 'pdf') {
            exportToPDF();
        }
    };

    // Pagination calculations
    const totalPages = Math.ceil(groupedData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = groupedData.slice(startIndex, endIndex);

    const getStockAdjustmentReport = useCallback(async () => {
        setLoading(true);
        try {
            // Use current filter values when function is called
            const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
            const toDate = filters.toDate ? new Date(filters.toDate) : null;
            
            const formatDate = (date) => {
                if (!date) return '';
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };
            
            const response = await axios.get(`${BASE_URL}${GET_STOCK_ADJUSTMENT_REPORT}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    'rpType': 'bill',
                    'fromDate': formatDate(fromDate),
                    'toDate': formatDate(toDate),
                    'departmentID': localStorage.getItem('department_id') || '',
                },
            });
            if (response.status === 200) {
                const data = response.data.data || [];
                // Sort by InvoiceNo in descending order before grouping
                const sortedData = data.sort((a, b) => {
                    const invoiceA = a.InvoiceNo || '';
                    const invoiceB = b.InvoiceNo || '';
                    // Convert to number if possible, otherwise compare as strings
                    const numA = parseInt(invoiceA) || 0;
                    const numB = parseInt(invoiceB) || 0;
                    if (numA !== 0 || numB !== 0) {
                        return numB - numA; // Descending order
                    }
                    return invoiceB.localeCompare(invoiceA); // Descending order for strings
                });
                setStockData(sortedData);
                // Reset to page 1 when new data is loaded
                setCurrentPage(1);
            } else {
                toast.error('Failed to fetch stock adjustment report', { autoClose: 1000 });
            }
        } catch (error) {
            console.error('Failed to fetch stock adjustment report:', error);
            toast.error('Failed to fetch stock adjustment report', { autoClose: 1000 });
        }
        finally {
            setLoading(false);
        }
    }, [filters, token]);

    // Call API on page load with today's date (only runs once on mount)
    useEffect(() => {
        getStockAdjustmentReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array ensures this only runs on mount


    return (
        <div className="flex h-full bg-background">
            {/* <LeftMenu /> */}
            <ToastContainer
                position="top-center"
                autoClose={1000}
                limit={1}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />
            
            {loading && <Spinner />}

            <ExportFormatModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                onSelectFormat={handleExportFormat}
            />

            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 mb-2">
                    <div className={`flex flex-col gap-3 ${embedded ? 'lg:flex-row lg:items-center lg:justify-end' : 'lg:flex-row lg:items-center lg:justify-between'}`}>
                        {!embedded && (
                            <div className="min-w-0 shrink-0">
                                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                                    Stock Adjustment Reports
                                </p>
                                <h1 className="text-lg font-bold text-slate-800">
                                    Stock Adjustment Bill Wise
                                </h1>
                            </div>
                        )}
                        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
                            {reportConfig.filters.map((filter) => {
                                if (filter.type === 'date') {
                                    return (
                                        <label key={filter.id} className="flex items-center gap-2 text-sm font-medium text-slate-700">
                                            <span className="whitespace-nowrap">{filter.label}:</span>
                                            <input
                                                type="date"
                                                value={filters[filter.id] ?? ''}
                                                onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                            />
                                        </label>
                                    );
                                }
                                return null;
                            })}
                            <button
                                type="button"
                                onClick={handleRunReport}
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600"
                            >
                                <FiRefreshCcw size={16} />
                                Generate
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setFilters(buildInitialFilters(reportConfig.filters));
                                    setSelectedAdjustmentTypes([]);
                                    setCurrentPage(1);
                                }}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                <FiDownloadCloud size={16} />
                                Export
                            </button>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-hidden flex flex-col">
                    <div className="flex flex-col gap-3 flex-1 min-h-0">
                        {/* Table Section */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm flex flex-col flex-1 min-h-0">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0 mb-4">
                                <div className="flex flex-col gap-2">
                                    <p className="text-sm text-slate-500">
                                        Showing <span className="font-semibold text-slate-700">{groupedData.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, groupedData.length)}</span> of <span className="font-semibold text-slate-700">{groupedData.length}</span> invoices with <span className="font-semibold text-slate-700">{filteredStockData.length}</span> items.
                                    </p>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Adjustment Type:</span>
                                        {ADJUSTMENT_TYPE_FILTERS.map((type) => {
                                            const isSelected = selectedAdjustmentTypes.includes(type.id);
                                            const colorClass = type.id === 'plus'
                                                ? isSelected
                                                    ? 'bg-green-100 text-green-700 border-green-300'
                                                    : 'border-slate-200 text-slate-600 hover:border-green-300 hover:text-green-600'
                                                : type.id === 'minus'
                                                    ? isSelected
                                                        ? 'bg-red-100 text-red-700 border-red-300'
                                                        : 'border-slate-200 text-slate-600 hover:border-red-300 hover:text-red-600'
                                                    : isSelected
                                                        ? 'bg-slate-200 text-slate-700 border-slate-400'
                                                        : 'border-slate-200 text-slate-600 hover:border-slate-400 hover:text-slate-700';

                                            return (
                                                <button
                                                    key={type.id}
                                                    type="button"
                                                    onClick={() => toggleAdjustmentType(type.id)}
                                                    className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold transition ${colorClass}`}
                                                >
                                                    {type.label}
                                                </button>
                                            );
                                        })}
                                        {selectedAdjustmentTypes.length > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedAdjustmentTypes([]);
                                                    setCurrentPage(1);
                                                }}
                                                className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
                                            >
                                                Clear filters
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                                                            currentPage === pageNum
                                                                ? 'bg-indigo-600 text-white border-indigo-600'
                                                                : 'border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
                                                        }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <span className="text-sm text-slate-600">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="mt-2 flex-1 overflow-auto min-h-0 space-y-4">
                                {groupedData.length > 0 ? (
                                    paginatedData.map((invoice, index) => (
                                        <div key={`${invoice.Sequence}_${invoice.InvoiceNo}`} className="rounded-xl border border-slate-200 overflow-hidden">
                                            {/* Invoice Header */}
                                            <div className="bg-indigo-50 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                                                <div className="flex items-center gap-4">
                                                    <span className="text-sm font-semibold text-indigo-700">
                                                        Entry No: {invoice.InvoiceNo}
                                                    </span>
                                                    <span className="text-sm text-slate-600">
                                                        Date: {new Date(invoice.EntryDate).toLocaleDateString('en-GB', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric'
                                                        })}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-4 flex-wrap">
                                                    <span className="text-sm text-slate-600">
                                                        Items: <span className="font-semibold">{invoice.items.length}</span>
                                                    </span>
                                                    <span className="text-sm text-green-600">
                                                        Plus: <span className="font-semibold">{invoice.items.reduce((sum, item) => sum + parseFloat(item.PlusQty || 0), 0).toFixed(2)}</span>
                                                    </span>
                                                    <span className="text-sm text-red-600">
                                                        Minus: <span className="font-semibold">{invoice.items.reduce((sum, item) => sum + parseFloat(item.MinusQty || 0), 0).toFixed(2)}</span>
                                                    </span>
                                                    <span className="text-sm text-green-700">
                                                        Plus Value: <span className="font-semibold">{invoice.items.reduce((sum, item) => sum + getPlusValue(item), 0).toFixed(2)}</span>
                                                    </span>
                                                    <span className="text-sm text-red-700">
                                                        Minus Value: <span className="font-semibold">{invoice.items.reduce((sum, item) => sum + getMinusValue(item), 0).toFixed(2)}</span>
                                                    </span>
                                                </div>
                                            </div>
                                            {/* Invoice Items Table */}
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-slate-200 text-sm">
                                                    <thead className="bg-slate-50">
                                                        <tr>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">SI No</th>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Item Name</th>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Batch No</th>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Expiry</th>
                                                            <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Old Stock</th>
                                                            <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Rate</th>
                                                            <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Plus Qty</th>
                                                            <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Minus Qty</th>
                                                            <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Plus Value</th>
                                                            <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Minus Value</th>
                                                            <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Adjusted Qty</th>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Remarks</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-slate-100">
                                                        {invoice.items.map((item, itemIndex) => (
                                                            <tr key={itemIndex} className="hover:bg-indigo-50/30">
                                                                <td className="px-4 py-2 text-slate-700">{itemIndex + 1}</td>
                                                                <td className="px-4 py-2 text-slate-700 font-medium">{item.HeaderItemName || '-'}</td>
                                                                <td className="px-4 py-2 text-slate-700">{item.BatchNo || '-'}</td>
                                                                <td className="px-4 py-2 text-slate-700">{item.ExpiryDate ? new Date(item.ExpiryDate).toLocaleDateString('en-GB', { month: '2-digit', year: 'numeric' }) : '-'}</td>
                                                                <td className="px-4 py-2 text-slate-700 text-right">{parseFloat(item.CurrentStock || 0).toFixed(2)}</td>
                                                                <td className="px-4 py-2 text-slate-700 text-right">{getRate(item).toFixed(2)}</td>
                                                                <td className="px-4 py-2 text-green-600 text-right font-medium">{parseFloat(item.PlusQty || 0).toFixed(2)}</td>
                                                                <td className="px-4 py-2 text-red-600 text-right font-medium">{parseFloat(item.MinusQty || 0).toFixed(2)}</td>
                                                                <td className="px-4 py-2 text-green-700 text-right font-medium">{getPlusValue(item).toFixed(2)}</td>
                                                                <td className="px-4 py-2 text-red-700 text-right font-medium">{getMinusValue(item).toFixed(2)}</td>
                                                                <td className="px-4 py-2 text-slate-700 text-right font-semibold">{parseFloat(item.AdjustedQty || 0).toFixed(2)}</td>
                                                                <td className="px-4 py-2 text-slate-500">{item.Remarks || '-'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="rounded-xl border border-slate-200 px-4 py-8 text-center text-slate-500">
                                        No data available for the selected period
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default withAuth(StockAdjustmentBillWise);